import type { NextRequest } from 'next/server';
import { LoginResponse } from '@/app/api/login/route';
import { BOTID_BLOCKED_ERROR_CODE } from '@/const/botid';
import { CACHE_TIME } from '@/const/cache';
import { LOGIN_SOURCE_HEADER, LoginSource } from '@/const/login';
import { User } from '@/types/User';

export const USER_INFO_KEY = 'currentUser_v1';

/**
 * Thrown when /api/login answers 403 `browser_verification_failed`, i.e.
 * BotID (enforce mode) classified the browser as automated. Kept distinct from
 * a credential failure so the form can say "refresh and retry" rather than
 * "wrong password".
 */
export class LoginBlockedError extends Error {
  constructor() {
    super('Login blocked by browser verification');
    this.name = 'LoginBlockedError';
  }
}

/**
 * Logs a user into the application. Stores the user into `localStorage`
 * if successful and returns the login response from the Next Server API route.
 * @param email
 * @param password
 * @param source which flow is logging in — a log tag for BotID monitoring
 * @returns
 */
export async function login(
  email: string,
  password: string,
  source: LoginSource = 'login_form'
): Promise<LoginResponse> {
  try {
    const response = await fetch('/api/login', {
      method: 'POST',
      headers: { [LOGIN_SOURCE_HEADER]: source },
      body: JSON.stringify({ email: email.toLowerCase(), password })
    });

    // TODO: Handle login response
    const body = (await response.json()) as LoginResponse;

    if (response.status === 403 && body.error === BOTID_BLOCKED_ERROR_CODE) {
      throw new LoginBlockedError();
    }
    // Store data in localStorage
    if (body.data) {
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(body.data));
    } else {
      console.error(
        'There was an error storing user information in localStorage'
      );
    }

    return body;
  } catch (error) {
    throw error;
  }
}

/**
 * Retrieves the user from localStorage. **Only usable on client**
 * @returns User | undefined
 */
export function getUserFromLocalStorage(): User | undefined {
  if (!localStorage) {
    return;
  }
  const value = localStorage.getItem(USER_INFO_KEY);

  if (!value) {
    return;
  }

  const user = JSON.parse(value) as User;
  const now = Date.now();

  if (!user?.tokenExpiry || (user.tokenExpiry && now > user.tokenExpiry)) {
    localStorage.removeItem(USER_INFO_KEY);
    return;
  }

  return user;
}

/**
 * Logs out server-side, then clears local state. Throws when the server did not
 * confirm (a non-2xx or a network error) and leaves `localStorage` alone, so
 * the caller can keep the member on the page with a retry instead of claiming
 * a logout that did not happen.
 */
export async function logout() {
  if (!localStorage) {
    return;
  }
  // Request to logout
  const response = await fetch('/api/logout', {
    method: 'POST',
    next: {
      revalidate: CACHE_TIME.NO_CACHE
    },
    headers: {
      'content-type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new LogoutFailedError(response.status);
  }

  localStorage.removeItem(USER_INFO_KEY);

  // Remove sessionStorage items that have potentially been set related to application flow
  sessionStorage.removeItem('signup_credentials');
  sessionStorage.removeItem('applying_from_privee_landing_page');
}

export class LogoutFailedError extends Error {
  status: number;

  constructor(status: number) {
    super(`Logout failed with status ${status}`);
    this.name = 'LogoutFailedError';
    this.status = status;
  }
}

export function isAuthorized(
  request: NextRequest,
  key: string,
  secret: string
): boolean {
  const authHeader = request.headers.get('Authorization');
  if (!authHeader) return false;

  const encodedCredentials = Buffer.from(`${key}:${secret}`).toString('base64');
  const expectedHeader = `Basic ${encodedCredentials}`;

  return authHeader === expectedHeader;
}
